import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import {
  HERO_DATA, WHAT_WE_DO, CORE_DIVISIONS, IMPACT_METRICS, HOME_PAGE_DATA
} from "@/data/siteData";
import { supabase } from "../lib/supabaseClient";
import {
  Code, GraduationCap, Trophy, Users, Monitor, Rocket, Briefcase,
  Zap, Network, Palette, ArrowRight, ChevronLeft, ChevronRight, Quote
} from "lucide-react";

const iconMap = { Code, GraduationCap, Trophy, Users, Monitor, Rocket, Briefcase, Zap, Network, Palette };

// --- NEW: Particle Beam & Light Ring Animation ---
const ParticleBeamRing = () => {
  const particles = Array.from({ length: 30 });

  return (
    <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-visible pointer-events-none">
      
      {/* 1. Core Glow (Pulsing center) */}
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(9,125,195,0.15) 0%, transparent 70%)",
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.6, 1, 0.6],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* 2. The "Smoke Ring" (SVG with animated stroke) */}
      <svg 
        className="absolute w-[500px] h-[500px]" 
        viewBox="0 0 200 200"
        style={{ filter: "drop-shadow(0 0 20px rgba(9, 125, 195, 0.4))" }}
      >
        <defs>
          <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#097DC3" stopOpacity="0" />
            <stop offset="50%" stopColor="#3DC0E7" stopOpacity="1" />
            <stop offset="100%" stopColor="#097DC3" stopOpacity="0" />
          </linearGradient>
          <filter id="blur">
            <feGaussianBlur in="SourceGraphic" stdDeviation="1" />
          </filter>
        </defs>
        
        {/* Outer Ring Flow */}
        <motion.circle
          cx="100"
          cy="100"
          r="80"
          fill="none"
          stroke="url(#ringGradient)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray="50 150"
          filter="url(#blur)"
          animate={{ rotate: 360 }}
          style={{ originX: "50%", originY: "50%" }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        />
        
        {/* Inner Ring Flow (Opposite direction) */}
        <motion.circle
          cx="100"
          cy="100"
          r="60"
          fill="none"
          stroke="#8CDDF6"
          strokeWidth="1"
          strokeDasharray="30 100"
          opacity="0.6"
          strokeLinecap="round"
          animate={{ rotate: -360 }}
          style={{ originX: "50%", originY: "50%" }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
        />
      </svg>

      {/* 3. Sunlight Beams (Rotating Container) */}
      <motion.div
        className="absolute w-[600px] h-[600px]"
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      >
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute top-1/2 left-1/2 h-[1px] origin-left"
            style={{
              width: "350px",
              background: "linear-gradient(90deg, rgba(140,221,246,0) 0%, rgba(140,221,246,0.2) 50%, rgba(140,221,246,0) 100%)",
              transform: `rotate(${i * 60}deg) translateX(-50%)`,
            }}
          />
        ))}
      </motion.div>

      {/* 4. Floating Light Particles */}
      {particles.map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-[3px] h-[3px] rounded-full bg-[#3DC0E7]"
          style={{
            boxShadow: "0 0 6px #3DC0E7",
            left: "50%",
            top: "50%",
          }}
          initial={{ 
            x: Math.random() * 300 - 150, 
            y: Math.random() * 300 - 150, 
            opacity: 0,
            scale: Math.random() * 0.5 + 0.5
          }}
          animate={{
            x: [null, Math.random() * 400 - 200],
            y: [null, Math.random() * 400 - 200],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: Math.random() * 5 + 5,
            repeat: Infinity,
            repeatType: "loop",
            ease: "easeInOut",
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
};

const CountUpNumber = ({ end, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime;
    const animate = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / 2000, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, end]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export default function Home() {
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadTestimonials() {
        setLoadingTestimonials(true);
        try {
          const { data, error } = await supabase
            .from("testimonials")
            .select("id,name,role,category,program,outcome,status")
            .eq("status", "approved")
            .order("approved_at", { ascending: false });

          if (error) throw error;
          if (!mounted) return;
          setTestimonials(Array.isArray(data) ? data : []);
        } catch {
          if (mounted) setTestimonials([]);
        } finally {
          if (mounted) setLoadingTestimonials(false);
        }
      }

    loadTestimonials();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;

    const interval = setInterval(() => {
      setTestimonialIdx((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    setTestimonialIdx(0);
  }, [testimonials.length]);

  return (
    <div>
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-white" data-testid="hero-section">
        {/* Top-left gradient */}
        <div className="absolute top-0 left-0 w-[35vw] h-[35vh] bg-gradient-to-br from-[#097DC3]/15 via-[#8CDDF6]/8 to-transparent blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-24 md:pt-12 md:pb-32 relative z-10 w-full">
          {/* Main Grid Container */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Column: Text Content */}
            <div className="max-w-xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#097DC3]/8 text-[#097DC3] text-sm font-semibold uppercase tracking-wider">
                  <span className="w-2 h-2 rounded-full bg-[#097DC3] animate-pulse" />
                  {HERO_DATA.tagline}
                </span>
              </motion.div>
              <motion.h1
                className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#0A0A0A] mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
              >
                {HOME_PAGE_DATA.hero.titlePrefix}{" "}
                <span className="text-[#097DC3]">{HOME_PAGE_DATA.hero.titleHighlight}</span>{HOME_PAGE_DATA.hero.titleSuffix}
              </motion.h1>
              <motion.p
                className="text-lg md:text-xl leading-relaxed text-[#475569] mb-10 max-w-2xl"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                {HERO_DATA.subtitle}
              </motion.p>
              <motion.div
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
              >
                <Link
                  to="/software-development"
                  className="btn-pill inline-flex items-center gap-2 bg-[#097DC3] text-white px-8 py-3.5 rounded-full font-semibold text-sm"
                  data-testid="hero-cta-primary"
                >
                  {HERO_DATA.cta_primary}
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/contact"
                  className="btn-pill inline-flex items-center gap-2 bg-white text-[#097DC3] border-2 border-[#097DC3] px-8 py-3.5 rounded-full font-semibold text-sm"
                  data-testid="hero-cta-secondary"
                >
                  {HERO_DATA.cta_secondary}
                </Link>
              </motion.div>
            </div>

            {/* Right Column: Logo & Animation */}
            <motion.div
              className="hidden lg:flex relative items-center justify-center h-[600px] w-full"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {/* Container for Centering */}
              <div className="relative w-[500px] h-[500px] flex items-center justify-center">
                
                {/* 1. Background Animation (z-0) */}
                <ParticleBeamRing />

                {/* 2. Logo Image (z-10) */}
                <img
                  src="/logo.png"
                  className="relative z-10 w-[350px] max-w-[80%] object-contain"
                  alt="Company Logo"
                />
                
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Rest of the page sections remain unchanged... */}
      
      <section className="py-12 md:py-16 bg-gradient-to-t from-[#097DC3]/20 via-[#8CDDF6]/10 to-white" data-testid="what-we-do-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{HOME_PAGE_DATA.sections.whatWeDo.label}</span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-4">
              {HOME_PAGE_DATA.sections.whatWeDo.title}
            </h2>
            <p className="text-base md:text-lg text-[#475569] max-w-2xl mb-16">
              {HOME_PAGE_DATA.sections.whatWeDo.description}
            </p>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {WHAT_WE_DO.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <StaggerItem key={item.title}>
                  <div className="card-lift h-full w-full bg-white p-6 rounded-2xl border border-slate-100 shadow-sm group">
                    <div className="w-12 h-12 rounded-xl bg-[#097DC3]/8 flex items-center justify-center mb-5 group-hover:bg-[#097DC3] transition-colors duration-300">
                      <Icon size={24} className="text-[#097DC3] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-3">{item.title}</h3>
                    <p className="text-sm text-[#475569] leading-relaxed">{item.description}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#CEE5F3]" data-testid="core-divisions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{HOME_PAGE_DATA.sections.coreDivisions.label}</span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-16">
              {HOME_PAGE_DATA.sections.coreDivisions.title}
            </h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {CORE_DIVISIONS.map((div) => {
              const Icon = iconMap[div.icon];
              return (
                <StaggerItem key={div.title}>
                  <div className="group">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#097DC3]/10 flex items-center justify-center shrink-0 mt-1">
                        <Icon size={20} className="text-[#097DC3]" />
                      </div>
                      <div>
                        <h3 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-2 group-hover:text-[#097DC3] transition-colors duration-200">
                          {div.title}
                        </h3>
                        <p className="text-sm text-[#475569] leading-relaxed">{div.description}</p>
                      </div>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#097DC3] relative overflow-hidden" data-testid="impact-metrics-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-white/20 blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-white/20 blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="text-sm font-semibold uppercase tracking-wider text-[#8CDDF6] mb-3 block">{HOME_PAGE_DATA.sections.impact.label}</span>
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-white">
                {HOME_PAGE_DATA.sections.impact.title}
              </h2>
            </div>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {IMPACT_METRICS.map((metric) => (
              <StaggerItem key={metric.label}>
                <div className="text-center">
                  <div className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mb-2">
                    <CountUpNumber end={metric.value} suffix={metric.suffix} />
                  </div>
                  <p className="text-sm text-[#8CDDF6] font-medium">{metric.label}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Featured Testimonials */}
      <section className="py-12 md:py-16 relative bg-fixed bg-center bg-cover" style={{ backgroundImage: HOME_PAGE_DATA.sections.testimonials.backgroundImage }} data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{HOME_PAGE_DATA.sections.testimonials.label}</span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-16">
              {HOME_PAGE_DATA.sections.testimonials.title}
            </h2>
          </ScrollReveal>
          <ScrollReveal>
            <div className="max-w-3xl mx-auto">
              <div className="relative bg-[#F8FAFC] p-8 md:p-12 rounded-2xl border-l-4 border-[#3DC0E7]">
                <Quote size={32} className="text-[#8CDDF6] mb-6" />
                {loadingTestimonials ? (
                  <p className="text-lg md:text-xl text-[#475569] leading-relaxed mb-2 min-h-[80px]" data-testid="testimonial-quote">
                    {HOME_PAGE_DATA.sections.testimonials.loading}
                  </p>
                ) : testimonials.length === 0 ? (
                  <p className="text-lg md:text-xl text-[#475569] leading-relaxed mb-2 min-h-[80px]" data-testid="testimonial-quote">
                    {HOME_PAGE_DATA.sections.testimonials.empty}
                  </p>
                ) : (
                  <>
                    <p className="text-lg md:text-xl text-[#0A0A0A] leading-relaxed mb-8 min-h-[80px]" data-testid="testimonial-quote">
                      {testimonials[testimonialIdx]?.outcome}
                    </p>
                    <div className="flex items-center gap-4">
                      <img
                        src={HOME_PAGE_DATA.sections.testimonials.avatarSrc}
                        alt={testimonials[testimonialIdx]?.name || HOME_PAGE_DATA.sections.testimonials.avatarAlt}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <p className="font-semibold text-[#0A0A0A]">{testimonials[testimonialIdx]?.name}</p>
                        <p className="text-sm text-[#475569]">{testimonials[testimonialIdx]?.role}</p>
                        <p className="text-xs text-[#097DC3] font-medium mt-0.5">{testimonials[testimonialIdx]?.program}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-8">
                      <div className="flex gap-2">
                        {testimonials.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setTestimonialIdx(i)}
                            className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${i === testimonialIdx ? "bg-[#097DC3]" : "bg-[#E2E8F0]"}`}
                            data-testid={`testimonial-dot-${i}`}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setTestimonialIdx((p) => (p - 1 + testimonials.length) % testimonials.length)}
                          className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                          data-testid="testimonial-prev"
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <button
                          onClick={() => setTestimonialIdx((p) => (p + 1) % testimonials.length)}
                          className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                          data-testid="testimonial-next"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#097DC3]/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-[#3DC0E7]/15 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6">
                {HOME_PAGE_DATA.sections.cta.title}
              </h2>
              <p className="text-lg text-slate-400 mb-10">
                {HOME_PAGE_DATA.sections.cta.description}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="btn-pill inline-flex items-center gap-2 bg-[#097DC3] text-white px-8 py-3.5 rounded-full font-semibold text-sm"
                  data-testid="cta-contact"
                >
                  {HOME_PAGE_DATA.sections.cta.primaryButton}
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/about"
                  className="btn-pill inline-flex items-center gap-2 bg-transparent text-white border border-slate-600 px-8 py-3.5 rounded-full font-semibold text-sm hover:border-[#3DC0E7]"
                  data-testid="cta-about"
                >
                  {HOME_PAGE_DATA.sections.cta.secondaryButton}
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}